import React, { useMemo, useState } from 'react';
import {
  ClipboardCheck, Download, Eye, FileSpreadsheet, FileText, FolderInput, Loader2, PencilRuler,
  ScanSearch, Trash2, Upload, Wrench,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { inquiriesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { InquiryDocumentPreviewDialog } from './InquiryDocumentPreviewDialog';
import { InquiryFeasibilityCard } from './InquiryFeasibilityCard';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'customer_inquiry', label: 'Customer inquiry / RFQ', icon: FileText, hint: 'The customer enquiry, RFQ or email attachment. Run AI extraction here to create items.' },
  { key: 'drawing', label: 'Drawings', icon: PencilRuler, hint: 'PDF, JPG, PNG or TIFF drawings — viewable in the browser without downloading.' },
  { key: 'technical', label: 'Technical documents', icon: Wrench, hint: 'Specifications, standards, material and process documents.' },
  { key: 'feasibility', label: 'Feasibility review', icon: ClipboardCheck, hint: 'Internal feasibility studies with your verdict.' },
  { key: 'costing_sheet', label: 'Costing sheets', icon: FileSpreadsheet, hint: 'Cost sheets kept for the record, including sources retained by the Costing imports tab.', also: ['external_costing_source'] },
  { key: 'customer_po', label: 'Customer POs', icon: ClipboardCheck, hint: 'Purchase order documents retained against this inquiry.' },
  { key: 'vendor_response', label: 'Vendor responses', icon: Upload, hint: 'Bought-out vendor offers and price confirmations.' },
  { key: 'other', label: 'Other', icon: FileText, hint: 'Anything else worth retaining against this inquiry.' },
];

const EXTRACTABLE = ['.pdf', '.xlsx', '.xls', '.csv', '.jpg', '.jpeg', '.png', '.webp'];
const isExtractable = (filename = '') => EXTRACTABLE.some((ext) => filename.toLowerCase().endsWith(ext));

export const InquiryDocumentsTab = ({ inquiryId, inquiry, documents, onRefresh }) => {
  const { isAdmin } = useAuth();
  const [uploadingSection, setUploadingSection] = useState(null);
  const [extractingId, setExtractingId] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [tagItem, setTagItem] = useState('');

  const items = inquiry?.products || [];
  const grouped = useMemo(() => {
    const map = Object.fromEntries(SECTIONS.map((section) => [section.key, []]));
    const aliases = {};
    SECTIONS.forEach((section) => (section.also || []).forEach((alias) => { aliases[alias] = section.key; }));
    documents.forEach((document) => {
      const key = map[document.category] ? document.category : (aliases[document.category] || 'other');
      map[key].push(document);
    });
    return map;
  }, [documents]);

  const upload = async (event, sectionKey) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploadingSection(sectionKey);
    try {
      for (const file of files) {
        await inquiriesAPI.uploadDocument(inquiryId, file, sectionKey, tagItem || undefined);
      }
      toast.success(`${files.length} file(s) added`, {
        description: sectionKey === 'customer_inquiry'
          ? 'Use "Extract items" to read parts from the document with AI.'
          : 'Open the eye icon to view it right here.',
      });
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSection(null);
      event.target.value = '';
    }
  };

  const extract = async (document) => {
    setExtractingId(document.id);
    try {
      await inquiriesAPI.extractDocumentItems(inquiryId, document.id);
      toast.info(`Reading ${document.filename}…`, { description: 'Large documents can take a couple of minutes. You can keep working.' });
      // Extraction runs in the background so big files never hit the 120s proxy timeout
      const deadline = Date.now() + 15 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const { data } = await inquiriesAPI.extractDocumentStatus(inquiryId, document.id);
        if (data.status === 'completed') {
          toast.success(`${data.items_added} inquiry item(s) extracted`, {
            description: 'Open the Items tab to review, link parts and continue to costing.',
          });
          await onRefresh();
          return;
        }
        if (data.status === 'failed') {
          toast.error(data.error || 'AI extraction failed', { duration: 8000 });
          await onRefresh();
          return;
        }
      }
      toast.error('Extraction is taking unusually long — refresh the page to check again', { duration: 8000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI extraction failed', { duration: 8000 });
      await onRefresh();
    } finally {
      setExtractingId(null);
    }
  };

  const retag = async (document, value) => {
    try {
      await inquiriesAPI.updateDocument(inquiryId, document.id, { inquiry_product_id: value || null });
      toast.success(value ? 'Document tagged to item' : 'Item tag removed');
      await onRefresh();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not tag this document'); }
  };

  const move = async (document, category) => {
    const target = SECTIONS.find((section) => section.key === category);
    try {
      await inquiriesAPI.updateDocument(inquiryId, document.id, { category });
      toast.success(`Moved to ${target?.label || category}`, { description: document.filename });
      await onRefresh();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not move this document'); }
  };

  const download = async (document) => {
    try {
      const response = await inquiriesAPI.downloadDocument(inquiryId, document.id);
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.filename; anchor.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const remove = async (document) => {
    if (document.retention_locked && !window.confirm(
      `${document.filename} is a retained source of a costing import or customer PO. Deleting it keeps the costing/PO record but removes the original file. Continue?`,
    )) return;
    try {
      await inquiriesAPI.deleteDocument(inquiryId, document.id);
      toast.success(document.retention_locked ? 'Retained source deleted' : 'Document removed');
      await onRefresh();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not remove document'); }
  };

  return (
    <div className="space-y-6" data-testid="inquiry-documents-tab">
      <div className="flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold">Inquiry document sections</h2>
          <p className="text-sm text-muted-foreground">
            Every file is retained permanently and viewable in the browser. Filed in the wrong place?
            Use <strong>Move to</strong> on the file to send it to the right section.
          </p>
        </div>
        {items.length > 0 && (
          <div>
            <label className="text-xs uppercase text-muted-foreground">Tag new uploads to item</label>
            <select className="mt-1 h-10 w-full min-w-[240px] border bg-white px-2 text-sm" value={tagItem}
              onChange={(event) => setTagItem(event.target.value)} data-testid="inquiry-document-item-tag-select">
              <option value="">No item (inquiry level)</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.internal_part_no || item.customer_part_no || 'Unlinked'} — {item.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {SECTIONS.map((section) => {
        const sectionDocuments = grouped[section.key] || [];
        const Icon = section.icon;
        return (
          <section key={section.key} className="border" data-testid={`document-section-${section.key}`}>
            <div className="flex flex-col gap-3 border-b bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 text-blue-700" />
                <div>
                  <div className="flex items-center gap-2 font-heading font-bold">{section.label}
                    <Badge variant="outline" data-testid={`document-section-count-${section.key}`}>{sectionDocuments.length}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{section.hint}</p>
                </div>
              </div>
              <label>
                <Button asChild size="sm" variant="outline" disabled={uploadingSection === section.key}>
                  <span data-testid={`upload-${section.key}-button`}>
                    {uploadingSection === section.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    Add files
                  </span>
                </Button>
                <input type="file" multiple className="hidden" onChange={(event) => upload(event, section.key)}
                  data-testid={`upload-${section.key}-input`} />
              </label>
            </div>

            {sectionDocuments.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground" data-testid={`document-section-empty-${section.key}`}>Nothing here yet.</p>
            ) : (
              <div className="divide-y">
                {sectionDocuments.map((document) => {
                  const taggedItem = items.find((item) => item.id === document.inquiry_product_id);
                  return (
                    <div key={document.id} className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center" data-testid={`inquiry-document-${document.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium" data-testid={`inquiry-document-name-${document.id}`}>{document.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {Math.round((document.file_size || 0) / 1024)} KB · {new Date(document.uploaded_at).toLocaleString('en-IN')} · {document.uploaded_by_name || '—'}
                          {taggedItem ? ` · ${taggedItem.internal_part_no || taggedItem.customer_part_no || 'item'}` : ''}
                        </p>
                      </div>
                      {document.retention_locked && (
                        <Badge variant="outline" className="text-xs" data-testid={`system-document-${document.id}`}>
                          {document.category === 'external_costing_source' ? 'Costing import source' : 'System retained'}
                        </Badge>
                      )}
                      {items.length > 0 && !document.retention_locked && (
                        <select className="h-9 max-w-[230px] border bg-white px-2 text-xs" value={document.inquiry_product_id || ''}
                          onChange={(event) => retag(document, event.target.value)} data-testid={`retag-document-${document.id}`}>
                          <option value="">Inquiry level</option>
                          {items.map((item) => (
                            <option key={item.id} value={item.id}>{item.internal_part_no || item.customer_part_no || 'Unlinked'} — {item.description?.slice(0, 28)}</option>
                          ))}
                        </select>
                      )}
                      {(!document.retention_locked || isAdmin()) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`move-document-${document.id}`}>
                              <FolderInput className="mr-2 h-4 w-4" /> Move to
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-60">
                            <DropdownMenuLabel className="text-xs">
                              Currently in {section.label}
                              {document.retention_locked ? ' · retained source (admin move)' : ''}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {SECTIONS.filter((option) => option.key !== section.key).map((option) => {
                              const OptionIcon = option.icon;
                              return (
                                <DropdownMenuItem key={option.key} onSelect={() => move(document, option.key)}
                                  data-testid={`move-document-${document.id}-to-${option.key}`}>
                                  <OptionIcon className="mr-2 h-4 w-4 text-blue-700" /> {option.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {section.key === 'customer_inquiry' && isExtractable(document.filename) && (
                        <Button size="sm" variant="outline" onClick={() => extract(document)} disabled={extractingId === document.id}
                          data-testid={`extract-inquiry-document-${document.id}`}>
                          {extractingId === document.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}
                          {extractingId === document.id ? 'Reading…' : 'Extract items'}
                        </Button>
                      )}
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setPreviewing(document)} title="View" data-testid={`view-inquiry-document-${document.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => download(document)} title="Download" data-testid={`download-inquiry-document-${document.id}`}><Download className="h-4 w-4" /></Button>
                        {document.retention_locked && (
                          <Badge className="bg-emerald-100 text-emerald-800" data-testid={`retained-inquiry-document-${document.id}`}>Retained</Badge>
                        )}
                        {(!document.retention_locked || isAdmin()) && (
                          <Button size="icon" variant="ghost" onClick={() => remove(document)}
                            title={document.retention_locked ? 'Delete retained source (admin)' : 'Remove'}
                            data-testid={`delete-inquiry-document-${document.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {section.key === 'feasibility' && (
              <InquiryFeasibilityCard inquiryId={inquiryId} review={inquiry?.feasibility_review}
                documents={sectionDocuments} onRefresh={onRefresh} />
            )}
          </section>
        );
      })}

      <InquiryDocumentPreviewDialog inquiryId={inquiryId} document={previewing}
        open={Boolean(previewing)} onOpenChange={(value) => !value && setPreviewing(null)} />
    </div>
  );
};
